import type { GridsterEngine } from './gridster-engine';
import type { GridsterItemInstance } from './gridster-item-instance';
import { GridsterPush, PUSH_DIRECTION } from './gridster-push';
import { cancelScroll } from './gridster-scroll';

type HandleName = 'n' | 'e' | 's' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export class GridsterResizable {
  private enabled = false;
  private pointerId: number | null = null;
  private directionFunction: ((clientX: number, clientY: number) => void) | null = null;
  private push: GridsterPush | null = null;
  private itemBackup = { x: 0, y: 0, cols: 0, rows: 0 };
  private startClientX = 0;
  private startClientY = 0;
  private itemTop = 0;
  private itemLeft = 0;
  private itemWidth = 0;
  private itemHeight = 0;
  private top = 0;
  private left = 0;
  private width = 0;
  private height = 0;
  private right = 0;
  private bottom = 0;
  private minWidth = 0;
  private minHeight = 0;

  constructor(
    private item: GridsterItemInstance,
    private gridster: GridsterEngine
  ) {}

  toggle(): void {
    const enableResize = this.item.canBeResized();
    if (!this.enabled && enableResize) {
      this.enabled = true;
      this.item.el.addEventListener('pointerdown', this.dragStart);
    } else if (this.enabled && !enableResize) {
      this.enabled = false;
      this.item.el.removeEventListener('pointerdown', this.dragStart);
    }
  }

  destroy(): void {
    this.toggle();
    if (this.item.isResizing) {
      this.stop();
    }
    this.push = null;
    this.directionFunction = null;
  }

  private dragStart = (e: PointerEvent): void => {
    if (this.gridster.dragInProgress || !this.gridster.el) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return;
    }

    const handleEl = (e.target as HTMLElement).closest<HTMLElement>('[data-handle]');
    const handle = handleEl?.dataset.handle as HandleName | undefined;
    if (!handle || !this.item.getResizableHandles()[handle]) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    const el = this.item.el;
    this.pointerId = e.pointerId;
    el.setPointerCapture(e.pointerId);
    document.addEventListener('pointermove', this.dragMove);
    document.addEventListener('pointerup', this.dragStop);
    document.addEventListener('pointercancel', this.dragStop);
    window.addEventListener('blur', this.cancelOnBlur);

    el.classList.add('gridster-item-resizing');
    this.item.isResizing = true;

    this.startClientX = e.clientX;
    this.startClientY = e.clientY;
    this.itemTop = this.item.top;
    this.itemLeft = this.item.left;
    this.itemWidth = this.item.width;
    this.itemHeight = this.item.height;
    this.top = this.item.top;
    this.left = this.item.left;
    this.width = this.item.width;
    this.height = this.item.height;
    this.right = this.item.left + this.item.width;
    this.bottom = this.item.top + this.item.height;

    const options = this.gridster.options;
    this.minWidth =
      this.gridster.positionXToPixels(this.item.$item.minItemCols ?? options.minItemCols) -
      options.margin;
    this.minHeight =
      this.gridster.positionYToPixels(this.item.$item.minItemRows ?? options.minItemRows) -
      options.margin;

    this.gridster.movingItem = this.item.$item;
    this.gridster.previewStyle();
    this.push = new GridsterPush(this.item, this.gridster);
    this.gridster.dragInProgress = true;
    this.itemBackup = { x: 0, y: 0, cols: 0, rows: 0 };
    this.directionFunction = this.getDirectionFunction(handle);
  };

  private dragMove = (e: PointerEvent): void => {
    if (!this.directionFunction || !this.push) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.directionFunction(e.clientX, e.clientY);
  };

  private dragStop = (e: PointerEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    this.stop();
  };

  private cancelOnBlur = (): void => {
    this.stop();
  };

  private stop(): void {
    cancelScroll();
    document.removeEventListener('pointermove', this.dragMove);
    document.removeEventListener('pointerup', this.dragStop);
    document.removeEventListener('pointercancel', this.dragStop);
    window.removeEventListener('blur', this.cancelOnBlur);

    const el = this.item.el;
    if (this.pointerId !== null && el.hasPointerCapture?.(this.pointerId)) {
      el.releasePointerCapture(this.pointerId);
    }
    this.pointerId = null;

    el.classList.remove('gridster-item-resizing');
    this.item.isResizing = false;
    this.gridster.dragInProgress = false;

    this.item.setSize();
    this.item.checkItemChanges(this.item.$item, this.item.item);
    this.push?.setPushedItems();
    this.push = null;
    this.directionFunction = null;
    this.gridster.flushItemChanges();

    this.gridster.movingItem = null;
    this.gridster.previewStyle();
  }

  private getDirectionFunction(handle: HandleName): (clientX: number, clientY: number) => void {
    switch (handle) {
      case 'n':
        return this.handleNorth;
      case 's':
        return this.handleSouth;
      case 'e':
        return this.handleEast;
      case 'w':
        return this.handleWest;
      case 'ne':
        return this.handleNorthEast;
      case 'nw':
        return this.handleNorthWest;
      case 'se':
        return this.handleSouthEast;
      default:
        return this.handleSouthWest;
    }
  }

  private getNewNorthPosition(clientY: number): number {
    this.top = this.itemTop + (clientY - this.startClientY);
    this.height = this.bottom - this.top;
    if (this.minHeight > this.height) {
      this.height = this.minHeight;
      this.top = this.bottom - this.minHeight;
    }
    const marginTop = this.gridster.options.pushItems ? this.gridster.options.margin : 0;
    return this.gridster.pixelsToPositionY(this.top + marginTop, Math.floor);
  }

  private getNewWestPosition(clientX: number): number {
    this.left = this.itemLeft + (clientX - this.startClientX);
    this.width = this.right - this.left;
    if (this.minWidth > this.width) {
      this.width = this.minWidth;
      this.left = this.right - this.minWidth;
    }
    const marginLeft = this.gridster.options.pushItems ? this.gridster.options.margin : 0;
    return this.gridster.pixelsToPositionX(this.left + marginLeft, Math.floor);
  }

  private getNewSouthPosition(clientY: number): number {
    this.height = this.itemHeight + (clientY - this.startClientY);
    if (this.minHeight > this.height) {
      this.height = this.minHeight;
    }
    this.bottom = this.top + this.height;
    const marginBottom = this.gridster.options.pushItems ? 0 : this.gridster.options.margin;
    return this.gridster.pixelsToPositionY(this.bottom + marginBottom, Math.ceil);
  }

  private getNewEastPosition(clientX: number): number {
    this.width = this.itemWidth + (clientX - this.startClientX);
    if (this.minWidth > this.width) {
      this.width = this.minWidth;
    }
    this.right = this.left + this.width;
    const marginRight = this.gridster.options.pushItems ? 0 : this.gridster.options.margin;
    return this.gridster.pixelsToPositionX(this.right + marginRight, Math.ceil);
  }

  private handleNorth = (clientX: number, clientY: number): void => {
    const newPosition = this.getNewNorthPosition(clientY);
    const $item = this.item.$item;
    if ($item.y !== newPosition) {
      this.makeItemBackup();
      $item.rows += $item.y - newPosition;
      $item.y = newPosition;
      if (!this.check(PUSH_DIRECTION.fromSouth)) {
        return;
      }
    }
    this.setItemTop(this.top);
    this.setItemHeight(this.height);
  };

  private handleSouth = (clientX: number, clientY: number): void => {
    const newPosition = this.getNewSouthPosition(clientY);
    const $item = this.item.$item;
    if ($item.y + $item.rows !== newPosition) {
      this.makeItemBackup();
      $item.rows = newPosition - $item.y;
      if (!this.check(PUSH_DIRECTION.fromNorth)) {
        return;
      }
    }
    this.setItemHeight(this.height);
  };

  private handleEast = (clientX: number): void => {
    const newPosition = this.getNewEastPosition(clientX);
    const $item = this.item.$item;
    if ($item.x + $item.cols !== newPosition) {
      this.makeItemBackup();
      $item.cols = newPosition - $item.x;
      if (!this.check(PUSH_DIRECTION.fromWest)) {
        return;
      }
    }
    this.setItemWidth(this.width);
  };

  private handleWest = (clientX: number): void => {
    const newPosition = this.getNewWestPosition(clientX);
    const $item = this.item.$item;
    if ($item.x !== newPosition) {
      this.makeItemBackup();
      $item.cols += $item.x - newPosition;
      $item.x = newPosition;
      if (!this.check(PUSH_DIRECTION.fromEast)) {
        return;
      }
    }
    this.setItemLeft(this.left);
    this.setItemWidth(this.width);
  };

  private handleNorthWest = (clientX: number, clientY: number): void => {
    const newNorth = this.getNewNorthPosition(clientY);
    const newWest = this.getNewWestPosition(clientX);
    const $item = this.item.$item;
    if ($item.y !== newNorth || $item.x !== newWest) {
      this.makeItemBackup();
      $item.rows += $item.y - newNorth;
      $item.y = newNorth;
      $item.cols += $item.x - newWest;
      $item.x = newWest;
      if (!this.check(PUSH_DIRECTION.fromSouth)) {
        return;
      }
    }
    this.setItemTop(this.top);
    this.setItemHeight(this.height);
    this.setItemLeft(this.left);
    this.setItemWidth(this.width);
  };

  private handleNorthEast = (clientX: number, clientY: number): void => {
    const newNorth = this.getNewNorthPosition(clientY);
    const newEast = this.getNewEastPosition(clientX);
    const $item = this.item.$item;
    if ($item.y !== newNorth || $item.x + $item.cols !== newEast) {
      this.makeItemBackup();
      $item.rows += $item.y - newNorth;
      $item.y = newNorth;
      $item.cols = newEast - $item.x;
      if (!this.check(PUSH_DIRECTION.fromSouth)) {
        return;
      }
    }
    this.setItemTop(this.top);
    this.setItemHeight(this.height);
    this.setItemWidth(this.width);
  };

  private handleSouthWest = (clientX: number, clientY: number): void => {
    const newSouth = this.getNewSouthPosition(clientY);
    const newWest = this.getNewWestPosition(clientX);
    const $item = this.item.$item;
    if ($item.y + $item.rows !== newSouth || $item.x !== newWest) {
      this.makeItemBackup();
      $item.rows = newSouth - $item.y;
      $item.cols += $item.x - newWest;
      $item.x = newWest;
      if (!this.check(PUSH_DIRECTION.fromNorth)) {
        return;
      }
    }
    this.setItemLeft(this.left);
    this.setItemHeight(this.height);
    this.setItemWidth(this.width);
  };

  private handleSouthEast = (clientX: number, clientY: number): void => {
    const newSouth = this.getNewSouthPosition(clientY);
    const newEast = this.getNewEastPosition(clientX);
    const $item = this.item.$item;
    if ($item.y + $item.rows !== newSouth || $item.x + $item.cols !== newEast) {
      this.makeItemBackup();
      $item.rows = newSouth - $item.y;
      $item.cols = newEast - $item.x;
      if (!this.check(PUSH_DIRECTION.fromNorth)) {
        return;
      }
    }
    this.setItemHeight(this.height);
    this.setItemWidth(this.width);
  };

  private check(direction: string): boolean {
    const push = this.push;
    if (!push) {
      return false;
    }
    push.pushItems(direction, this.gridster.options.disablePushOnResize);
    if (this.gridster.checkCollision(this.item.$item)) {
      this.resetItem();
      return false;
    }
    this.gridster.previewStyle();
    push.checkPushBack();
    return true;
  }

  private makeItemBackup(): void {
    const $item = this.item.$item;
    this.itemBackup = { x: $item.x, y: $item.y, cols: $item.cols, rows: $item.rows };
  }

  private resetItem(): void {
    const options = this.gridster.options;
    const $item = this.item.$item;
    $item.x = this.itemBackup.x;
    $item.y = this.itemBackup.y;
    $item.cols = this.itemBackup.cols;
    $item.rows = this.itemBackup.rows;
    this.left = this.gridster.positionXToPixels($item.x);
    this.top = this.gridster.positionYToPixels($item.y);
    this.width = this.gridster.positionXToPixels($item.cols) - options.margin;
    this.height = this.gridster.positionYToPixels($item.rows) - options.margin;
    this.setItemTop(this.top);
    this.setItemLeft(this.left);
    this.setItemWidth(this.width);
    this.setItemHeight(this.height);
  }

  private setItemTop(top: number): void {
    this.gridster.gridRenderer.setCellPosition(this.item.el, this.left, top);
  }

  private setItemLeft(left: number): void {
    this.gridster.gridRenderer.setCellPosition(this.item.el, left, this.top);
  }

  private setItemWidth(width: number): void {
    this.item.el.style.width = `${width}px`;
  }

  private setItemHeight(height: number): void {
    this.item.el.style.height = `${height}px`;
  }
}
